// @mui
import Card from '@mui/material/Card';
import { Box, Paper, TextField, Grid, Stack, Button, Typography, CircularProgress } from '@mui/material';
import CardContent from '@mui/material/CardContent';
// hooks
import { useEffect, useState, useRef } from 'react';
// components
import Iconify from 'src/components/iconify';
import { getRequest, postRequestForWebsite } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { handleErrorMessages } from 'src/services/fetching';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import useChallengeSection from 'src/hooks/use-challenge-page';

// ----------------------------------------------------------------------

export default function Challenge() {
   const { enqueueSnackbar } = useSnackbar();
  const { fetchOnMount } = useChallengeSection();
  const challengePageData = useSelector((state) => state.website.challengePage);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    // Main Header
    mainTitle: "",
    mainTitleColor: "#000000",
    subtitle: "",
    subtitleColor: "#000000",

    // Main Description
    mainDescription: "",
    mainDescriptionColor: "#555555",

    // Key Benefits Section
    benefitsTitle: "",
    benefitsTitleColor: "#000000",

    // Benefit Cards
    benefitCards: [
      {
        // id: '',
        title: "",
        titleColor: "#000000",
        description: "",
        descriptionColor: "#555555",
        // backgroundColor: "#f8f9fa",
        borderColor: "#e9ecef"
      },
      {
        id: '',
        title: "",
        titleColor: "#000000",
        description: "",
        descriptionColor: "#555555",
        backgroundColor: "#f8f9fa",
        borderColor: "#e9ecef"
      }
    ],

    // Features Section
    featuresTitle: "",
    featuresTitleColor: "#000000",

    features: [
      {
        id: '',
        text: "",
        textColor: "#555555"
      },

    ],

    // Efficiency Section
    efficiencyTitle: "",
    efficiencyTitleColor: "#000000",
    efficiencyDescription: "",
    efficiencyDescriptionColor: "#555555",

    // Why EZELOGS Section
    whyTitle: "",
    whyTitleColor: "#000000",
    whyDescription: "",
    whyDescriptionColor: "#555555",

    // General Styling
    // containerBackgroundColor: "#ffffff",
    // borderRadius: 1
  });


    useEffect(() => {
      fetchOnMount();
    }, [fetchOnMount]);
    useEffect(() => {
      setFormData(prev => ({
        ...prev,
        ...challengePageData?.challenge_page
      }));
    }, [challengePageData])

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (typeof current[key] !== 'object' || Array.isArray(current[key])) {
          current[key] = {};
        }
        current = current[key];
      }
      current[keys[keys.length - 1]] = value;

      return newData;
    });
  };

  const updateBenefitCard = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      benefitCards: prev.benefitCards.map((card, i) =>
        i === index ? { ...card, [field]: value } : card
      )
    }));
  };

  const updateFeature = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.map((feature, i) =>
        i === index ? { ...feature, [field]: value } : feature
      )
    }));
  };

  const addBenefitCard = () => {
    const newCard = {
      id: `benefit-${Date.now()}`,
      title: "",
      titleColor: "#000000",
      description: "",
      descriptionColor: "#555555",
      backgroundColor: "#f8f9fa",
      borderColor: "#e9ecef"
    };

    setFormData(prev => ({
      ...prev,
      benefitCards: [...prev.benefitCards, newCard]
    }));
  };

  const removeBenefitCard = (index) => {
    setFormData(prev => ({
      ...prev,
      benefitCards: prev.benefitCards.filter((_, i) => i !== index)
    }));
  };

  const addFeature = () => {
    const newFeature = {
      id: `feature-${Date.now()}`,
      text: "",
      textColor: "#555555"
    };

    setFormData(prev => ({
      ...prev,
      features: [...prev.features, newFeature]
    }));
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
   setLoading(true);

    try {
      // Create a FormData object for file uploads
      const formDataToSend = new FormData();

      // Append all form data
      Object.entries(formData).forEach(([key, value]) => {
        // Skip heroImage as we'll handle it separately
        if (key !== 'heroImage') {
          // Handle nested objects like primaryButton and secondaryButton
          if (typeof value === 'object' && value !== null && !(value instanceof File)) {
            formDataToSend.append(key, JSON.stringify(value));
          } else {
            formDataToSend.append(key, value);
          }
        }
      });

      // Append the hero image if it exists
      if (formData.heroImage) {
        formDataToSend.append('heroImage', formData.heroImage);
      }

      // Make sure heroImageUrl is included in the submission
      if (formData.heroImageUrl) {
        formDataToSend.append('heroImageUrl', formData.heroImageUrl);
      }

      const response = await postRequestForWebsite(
        endpoints.admin_dashboard.website.challenge_page.challenge_section,
        // formDataToSend,
        // dataForHeroSection,
        {
          attributes: {
            ...JSON.parse(JSON.stringify(challengePageData)),
            challenge_page: formData
          }
        },
        true
      );

      if (response.metadata?.success) {
        enqueueSnackbar({
          variant: 'success',
          message: response.metadata.message,
        });
      } else {
        handleErrorMessages(response.message || 'An error occurred.');
      }
    } catch (err) {
      console.error('Error:', err);
      handleErrorMessages(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h4" paragraph>
          Challenges Page Management
        </Typography>

        {/* Main Header Section */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Main Header</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Main Title"
                value={formData.mainTitle}
                onChange={(e) => updateFormData('mainTitle', e.target.value)}
                placeholder="Enter main title"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Main Title Color"
                type="color"
                value={formData.mainTitleColor}
                onChange={(e) => updateFormData('mainTitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Subtitle"
                value={formData.subtitle}
                onChange={(e) => updateFormData('subtitle', e.target.value)}
                placeholder="Enter subtitle"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Subtitle Color"
                type="color"
                value={formData.subtitleColor}
                onChange={(e) => updateFormData('subtitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Main Description"
                multiline
                rows={4}
                value={formData.mainDescription}
                onChange={(e) => updateFormData('mainDescription', e.target.value)}
                placeholder="Enter main description"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Description Color"
                type="color"
                value={formData.mainDescriptionColor}
                onChange={(e) => updateFormData('mainDescriptionColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Key Benefits Section */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center">
              <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
              <Typography variant="h6">Key Benefits Section</Typography>
            </Stack>
            <Button
              variant="contained"
              color="success"
              startIcon={<Iconify icon="mingcute:add-line" />}
              onClick={addBenefitCard}
            >
              Add Benefit Card
            </Button>
          </Stack>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Benefits Section Title"
                value={formData.benefitsTitle}
                onChange={(e) => updateFormData('benefitsTitle', e.target.value)}
                placeholder="Enter benefits title"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Benefits Title Color"
                type="color"
                value={formData.benefitsTitleColor}
                onChange={(e) => updateFormData('benefitsTitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
          </Grid>

          {/* Benefit Cards */}
          {formData.benefitCards.map((card, index) => (
            <Paper
              key={card.id}
              variant="outlined"
              sx={{ p: 3, mb: 3, bgcolor: 'background.neutral' }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" color="primary">
                  Benefit Card {index + 1}: {card.title || 'Untitled'}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  variant="contained"
                  onClick={() => removeBenefitCard(index)}
                  startIcon={<Iconify icon="mingcute:close-line" />}
                >
                  Remove
                </Button>
              </Stack>

              <Grid container spacing={2}>
                {/* <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Card ID"
                    value={card.id}
                    onChange={(e) => updateBenefitCard(index, 'id', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Background Color"
                    type="color"
                    value={card.backgroundColor}
                    onChange={(e) => updateBenefitCard(index, 'backgroundColor', e.target.value)}
                    sx={{ '& input': { height: 20 } }}
                  />
                </Grid> */}

                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Title"
                    value={card.title}
                    onChange={(e) => updateBenefitCard(index, 'title', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Title Color"
                    type="color"
                    value={card.titleColor}
                    onChange={(e) => updateBenefitCard(index, 'titleColor', e.target.value)}
                    sx={{ '& input': { height: 20 } }}
                  />
                </Grid>

                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    value={card.description}
                    onChange={(e) => updateBenefitCard(index, 'description', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Description Color"
                    type="color"
                    value={card.descriptionColor}
                    onChange={(e) => updateBenefitCard(index, 'descriptionColor', e.target.value)}
                    sx={{ '& input': { height: 20 } }}
                  />
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Box>

        {/* Features Section */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center">
              <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
              <Typography variant="h6">Features Section</Typography>
            </Stack>
            <Button
              variant="contained"
              color="success"
              startIcon={<Iconify icon="mingcute:add-line" />}
              onClick={addFeature}
            >
              Add Feature
            </Button>
          </Stack>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Features Section Title"
                value={formData.featuresTitle}
                onChange={(e) => updateFormData('featuresTitle', e.target.value)}
                placeholder="Enter features title"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Features Title Color"
                type="color"
                value={formData.featuresTitleColor}
                onChange={(e) => updateFormData('featuresTitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
          </Grid>


          {/* Features List */}
          {formData.features.map((feature, index) => (
            <Paper
              key={feature.id}
              variant="outlined"
              sx={{ p: 3, mb: 2, bgcolor: 'background.neutral' }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" color="primary">
                  Feature {index + 1}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => removeFeature(index)}
                  startIcon={<Iconify icon="mingcute:close-line" />}
                >
                  Remove
                </Button>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Feature Text"
                    multiline
                    rows={2}
                    value={feature.text}
                    onChange={(e) => updateFeature(index, 'text', e.target.value)}
                    placeholder="Enter feature description"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Text Color"
                    type="color"
                    value={feature.textColor}
                    onChange={(e) => updateFeature(index, 'textColor', e.target.value)}
                    sx={{ '& input': { height: 40 } }}
                  />
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Box>

        {/* Efficiency Section */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Efficiency Section</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Efficiency Title"
                value={formData.efficiencyTitle}
                onChange={(e) => updateFormData('efficiencyTitle', e.target.value)}
                placeholder="Enter efficiency title"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Title Color"
                type="color"
                value={formData.efficiencyTitleColor}
                onChange={(e) => updateFormData('efficiencyTitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Efficiency Description"
                multiline
                rows={4}
                value={formData.efficiencyDescription}
                onChange={(e) => updateFormData('efficiencyDescription', e.target.value)}
                placeholder="Enter efficiency description"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Description Color"
                type="color"
                value={formData.efficiencyDescriptionColor}
                onChange={(e) => updateFormData('efficiencyDescriptionColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Why EZELOGS Section */}
        <Box sx={{ mb: 5, pb: 3}}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Why EZELOGS Section</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Why EZELOGS Title"
                value={formData.whyTitle}
                onChange={(e) => updateFormData('whyTitle', e.target.value)}
                placeholder="Enter why title"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Title Color"
                type="color"
                value={formData.whyTitleColor}
                onChange={(e) => updateFormData('whyTitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Why EZELOGS Description"
                multiline
                rows={3}
                value={formData.whyDescription}
                onChange={(e) => updateFormData('whyDescription', e.target.value)}
                placeholder="Enter why description"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Description Color"
                type="color"
                value={formData.whyDescriptionColor}
                onChange={(e) => updateFormData('whyDescriptionColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* General Styling */}
        {/* <Box sx={{ mb: 5 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">General Styling</Typography>
          </Stack>


          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Container Background Color"
                type="color"
                value={formData.containerBackgroundColor}
                onChange={(e) => updateFormData('containerBackgroundColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Border Radius"
                type="number"
                value={formData.borderRadius}
                onChange={(e) => updateFormData('borderRadius', parseInt(e.target.value))}
                placeholder="Enter border radius"
              />
            </Grid>
          </Grid>
        </Box> */}

        {/* Action Buttons */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
          sx={{ pt: 3, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <LoadingButton
            loading={loading}
            variant="contained"
            color="primary"
            onClick={handleSave}
            startIcon={<Iconify icon="eva:save-fill" />}
            size="large"
          >
            Update
          </LoadingButton>
        </Stack>
      </CardContent>
    </Card>
  );
}
